import { AddonLfShareData, ShareLayoutSetting } from '@/components/addons/lf-share/Data';
import AddonModel from '@/models/grid/AddonModel';
import { AddonModelState } from '@/typings/interfaces/state/Addon';
import { getDeviceData } from '@/hooks/useDevice';
import { AlignContentType } from '@/typings/enums/enums';
import { CSSProperties } from 'vue';

export interface Facebook {
  enabled: boolean;
}

export interface Twitter {
  enabled: boolean;
  message?: string;
}

export interface Linkedin {
  enabled: boolean;
  title?: string;
  description?: string;
}

export interface Messenger {
  enabled: boolean;
}

export interface Whatsapp {
  enabled: boolean;
  message?: string;
}

export interface Email {
  enabled: boolean;
  subject?: string;
  body?: string;
}

export interface CustomShareUrl {
  enabled?: boolean;
  url?: string;
}

export interface Layout {
  iconColor: string;
  iconBackgroundColor: string;
  iconSize: string;
  alignment: string;
}

interface AddonLFShareState extends AddonModelState {
  facebook?: Facebook;
  twitter?: Twitter;
  linkedin?: Linkedin;
  messenger?: Messenger;
  whatsapp?: Whatsapp;
  email?: Email;
  customShareUrl?: CustomShareUrl;
  layout?: Layout;
  title?: string;
  description?: string;
  image?: string;
  campaignShareUrl?: string;
  shareClasses: string[];
  elementStyling: {
    shareIconStyle?: CSSProperties;
    shareItemStyle?: CSSProperties;
  };
}

export interface FacebookResponse {
  message: string;
}

export class AddonLFShareModel extends AddonModel<AddonLfShareData, AddonLFShareState> {
  parseAddon(data: AddonLfShareData) {
    const state = this.state;

    state.title = data.settings?.title;
    state.campaignShareUrl = data.settings?.campaign_share_url;
    state.description = data.settings?.description;

    state.facebook = {
      enabled: data.settings?.facebook?.enabled === '1'
    };

    state.email = {
      enabled: data.settings?.email?.enabled === '1',
      body: data.settings?.email?.body ?? '',
      subject: data.settings?.email?.subject ?? ''
    };

    state.twitter = {
      enabled: data.settings?.twitter?.enabled === '1',
      message: data.settings?.twitter?.message ?? undefined
    };

    state.linkedin = {
      enabled: data.settings?.linkedin?.enabled === '1',
      title: data.settings?.linkedin?.title,
      description: data.settings?.linkedin?.description
    };

    state.whatsapp = {
      enabled: data.settings?.whatsapp?.enabled === '1',
      message: data.settings?.whatsapp?.message ?? undefined
    };

    state.messenger = {
      enabled: data.settings?.messenger?.enabled === '1'
    };

    state.customShareUrl = {
      enabled: !!data.settings?.custom_share_url?.url,
      url: data.settings?.custom_share_url?.url
    };

    if (data.settings?.image) {
      state.image = data.settings.image;
    } else {
      state.image = undefined;
    }

    if (data.settings?.layout) {
      state.layout = AddonLFShareModel.parseLayoutSettings(data);
    } else {
      state.layout = undefined;
    }
    state.shareClasses = [];
    if (state.layout && state.layout?.alignment) {
      state.shareClasses.push(`content__item--share-${state.layout.alignment}`);
    } else {
      state.shareClasses.push(`content__item--share-${AlignContentType.CENTER}`);
    }
  }

  public isAddonValid(): boolean {
    const data = this.getData();
    const hasEnabledMethod =
      data.settings?.facebook?.enabled === '1' ||
      data.settings?.twitter?.enabled === '1' ||
      data.settings?.linkedin?.enabled === '1' ||
      data.settings?.messenger?.enabled === '1' ||
      data.settings?.whatsapp?.enabled === '1' ||
      data.settings?.email?.enabled === '1';

    return hasEnabledMethod;
  }

  private static parseLayoutSettings(data: AddonLfShareData): Layout | undefined {
    if (data.settings?.layout?.settings) {
      const useData = getDeviceData(data.settings.layout.settings);

      if (!useData) {
        return undefined;
      }

      if (useData) {
        return AddonLFShareModel.constructLayout(useData);
      }
    }

    return undefined;
  }

  private static constructLayout(data: ShareLayoutSetting): Layout {
    return {
      alignment: data.alignment,
      iconBackgroundColor: data.icon_background_color,
      iconColor: data.icon_color,
      iconSize: data.icon_size
    };
  }

  authorSignature(): string {
    return 'Nicky Christensen';
  }
}
